import { useState, type FormEvent } from 'react'
import { SALON_NOTE_MAX, type SalonNote } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate, formatTime } from '@/shared/lib/format.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { isOpenReminder } from '../reminders.ts'
import { useDeleteSalonNote, useUpdateSalonNote } from '../use-salon-notes.ts'
import { Counter, ReminderLine, errorText, linkButtonClass, submitOnModEnter, textareaClass } from './note-parts.tsx'
import { ReminderPicker } from './reminder-picker.tsx'

type Mode = 'view' | 'edit' | 'confirm-delete' | 'remind'

interface NoteItemProps {
  note: SalonNote
  today: string
  /** The signed-in person wrote it: may edit the text and manage the reminder. */
  own: boolean
  mayDelete: boolean
  /** Tells the dialog that something is being typed here, so Esc does not throw it away. */
  onBusyChange: (busy: boolean) => void
}

/** One comment in the thread: text, its reminder and what the author (or an owner) may do with them. */
export function NoteItem({ note, today, own, mayDelete, onBusyChange }: NoteItemProps) {
  const [mode, setModeState] = useState<Mode>('view')
  const [text, setText] = useState(note.text)
  const update = useUpdateSalonNote()
  const remove = useDeleteSalonNote()
  const pending = update.isPending || remove.isPending
  const openReminder = isOpenReminder(note)

  const setMode = (next: Mode) => {
    update.reset()
    remove.reset()
    setModeState(next)
    onBusyChange(next === 'edit' || next === 'remind')
  }

  function saveText(event: FormEvent) {
    event.preventDefault()
    const trimmed = text.trim()
    if (!trimmed || pending) return
    if (trimmed === note.text) {
      setMode('view')
      return
    }
    update.mutate({ id: note.id, patch: { text: trimmed } }, { onSuccess: () => setMode('view') })
  }

  const remindOn = (day: string | null) => {
    if (day === note.remindOn && openReminder) {
      setMode('view')
      return
    }
    update.mutate({ id: note.id, patch: { remindOn: day } }, { onSuccess: () => setMode('view') })
  }

  const error = update.error ?? remove.error

  return (
    <li className="flex flex-col gap-1.5 py-3.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px]">
        <span className="font-semibold text-ink">{note.authorName}</span>
        <span className="text-ink-muted">
          {formatDate(note.createdAt)}, {formatTime(note.createdAt)}
          {note.editedAt && ' · изменён'}
        </span>
      </div>

      {mode === 'edit' ? (
        <form onSubmit={saveText} className="flex flex-col gap-2">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={submitOnModEnter}
            rows={3}
            maxLength={SALON_NOTE_MAX}
            autoFocus
            aria-label="Текст комментария"
            className={textareaClass}
          />
          <div className="flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
            <Counter length={text.length} />
            <Button variant="secondary" size="sm" disabled={pending} onClick={() => setMode('view')}>
              Отмена
            </Button>
            <Button type="submit" size="sm" loading={update.isPending} disabled={!text.trim()}>
              Сохранить
            </Button>
          </div>
        </form>
      ) : (
        <p className="text-[14.5px] whitespace-pre-line text-ink [overflow-wrap:anywhere]">{note.text}</p>
      )}

      <ReminderLine note={note} today={today} own={own} />

      {mode === 'remind' && (
        <div className="flex flex-col gap-2 rounded-control bg-surface-hover/60 p-2.5">
          <span className="text-[12.5px] text-ink-soft">{openReminder ? 'Перенести напоминание на…' : 'Напомнить вам…'}</span>
          <ReminderPicker value={openReminder ? note.remindOn : null} today={today} onChange={remindOn} disabled={pending} />
          <div className="flex gap-3 text-[13px]">
            <button type="button" className={linkButtonClass} disabled={pending} onClick={() => setMode('view')}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {mode === 'confirm-delete' && (
        <div className="flex flex-wrap items-center gap-2 text-[13px] max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
          <span className="w-full text-ink-soft sm:w-auto">Удалить комментарий?</span>
          <Button
            variant="secondary"
            size="sm"
            loading={remove.isPending}
            onClick={() => remove.mutate(note.id)}
            className="text-warn-ink"
          >
            Удалить
          </Button>
          <Button variant="ghost" size="sm" disabled={pending} onClick={() => setMode('view')}>
            Отмена
          </Button>
        </div>
      )}

      {mode === 'view' && (own || mayDelete) && (
        <div className="flex flex-wrap gap-x-3 text-[13px]">
          {own && openReminder && note.remindOn <= today && (
            <button
              type="button"
              className={cn(linkButtonClass, 'text-brand hover:text-brand-dark')}
              disabled={pending}
              onClick={() => update.mutate({ id: note.id, patch: { reminderDone: true } })}
            >
              Готово
            </button>
          )}
          {own && (
            <button type="button" className={linkButtonClass} onClick={() => setMode('remind')}>
              {openReminder ? 'Перенести' : 'Напомнить…'}
            </button>
          )}
          {own && openReminder && (
            <button type="button" className={linkButtonClass} disabled={pending} onClick={() => remindOn(null)}>
              Снять напоминание
            </button>
          )}
          {own && (
            <button
              type="button"
              className={linkButtonClass}
              onClick={() => {
                setText(note.text)
                setMode('edit')
              }}
            >
              Изменить
            </button>
          )}
          {mayDelete && (
            <button type="button" className={linkButtonClass} onClick={() => setMode('confirm-delete')}>
              Удалить
            </button>
          )}
        </div>
      )}

      {error && <Alert>{errorText(error)}</Alert>}
    </li>
  )
}
